import React from 'react'
import { useSelector } from 'react-redux'
import { useCheckEditCampaignPermission, useCheckSomePermissions, usePermission } from 'hooks/permissions'
import { CAMPAIGN_STATUS_ENUM, selectEditingCampaign } from 'state/campaigns'
import { CampaignManagement, CampaignStatusControl } from './enums'
import { isCampaignLiveStatus } from 'components/utils'

interface CampaignEditorInputFieldContainerProp {
    isEditDeadline?: boolean
    isEditBrief?: boolean
    launchFieldEditable?: boolean
}

const CampaignEditorInputFieldContainer: React.FC<CampaignEditorInputFieldContainerProp> = ({
    children,
    isEditBrief = false,
    isEditDeadline = false,
    launchFieldEditable = false,
}) => {
    const campaign = useSelector(selectEditingCampaign)
    const isPermissionEditable = useCheckEditCampaignPermission(
        (campaign && campaign.status) || undefined,
        isEditBrief, //this is for when updating the create and edit brief permission
        isEditDeadline,
        launchFieldEditable,
    )
    const isCampaignLive = isCampaignLiveStatus(campaign?.status)
    const canPublishOrEditLive =
        useCheckSomePermissions([
            CampaignStatusControl.LaunchCampaign,
            CampaignManagement.LiveCampaignEditBriefs,
            CampaignManagement.LiveCampaignEditDeadlines,
        ]) && isCampaignLive
    const canPublishAndEditReview =
        usePermission(CampaignStatusControl.LaunchCampaign) && campaign?.status === CAMPAIGN_STATUS_ENUM.FOR_REVIEW
    const canEditDraft =
        useCheckSomePermissions([
            CampaignManagement.CreateAndEditBriefsForInternal,
            CampaignManagement.EditBriefsForExternal,
        ]) &&
        campaign?.status !== undefined &&
        [CAMPAIGN_STATUS_ENUM.DRAFT, CAMPAIGN_STATUS_ENUM.FOR_EDIT].includes(campaign?.status)
    const canEditWaitForLaunch =
        usePermission(CampaignStatusControl.LaunchCampaign) &&
        campaign?.status === CAMPAIGN_STATUS_ENUM.READY_FOR_LAUNCH

    const isCampaignEditable = canEditDraft || canPublishAndEditReview || canPublishOrEditLive || canEditWaitForLaunch

    const isReadOnly =
        campaign?.status === undefined ? !isPermissionEditable : !isCampaignEditable || !isPermissionEditable

    return (
        <>
            {typeof children === 'function'
                ? children(isReadOnly)
                : React.Children.map(children, (child: any) => {
                      const { isReadOnly: childIsReadyOnly } = child.props
                      return React.cloneElement(child, {
                          isReadOnly: childIsReadyOnly !== undefined ? childIsReadyOnly : isReadOnly,
                      })
                  })}
        </>
    )
}

export default CampaignEditorInputFieldContainer
